/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Learn URL construction (UCR-2725 shape, extracted in UCR-3040).
 *
 * Lifted out of `components/article-tile` so the sitemap builds the same paths the tiles link to,
 * character for character. A second implementation would drift, and the failure mode is quiet: a
 * sitemap full of URLs that 301 (or 404) while every on-site link keeps working.
 *
 * Parameters are typed **structurally** rather than as `LearnArticle`, so this module never imports
 * `catalog.server.ts`. That keeps it safe to pull into client bundles — the tiles are client
 * components — without dragging a `.server` module into the graph. `lib/seo/canonical-case` is pure
 * for the same reason.
 */
import { toCanonicalCase } from '@/lib/seo/canonical-case';

/** The Learn listing root. Also the URL for the `/learn` index itself. */
export const LEARN_ROOT = '/learn';

/**
 * URL for a Learn folder listing: `/learn/hydroponics`, `/learn/growing/hydroponics`.
 *
 * An empty `folderPath` is the library root, which is {@link LEARN_ROOT} rather than `/learn/`  —
 * the trailing slash would be a second URL for the same page.
 */
export function learnFolderUrl(folderPath: string): string {
    return toCanonicalCase(folderPath ? `${LEARN_ROOT}/${folderPath}` : LEARN_ROOT);
}

/**
 * Canonical URL for an Article page: `/learn/<folderPath>/<pageId>`, or `/learn/<pageId>` when the
 * page is filed at the library root.
 *
 * The page id *is* the slug — Page Designer page ids are merchandiser-authored and double as the URL
 * segment (see `ai-docs/learn-article-urls/`).
 *
 * Emitted in **canonical (lower) case**, because `/learn/**` is lowercase-canonical: an id authored as
 * `Making-Sense-Of-Microbes` must be linked and published as `/learn/making-sense-of-microbes`, or
 * every tile link and sitemap entry would take a 301 hop through the canonical-case middleware. The
 * Learn route resolves the lowercase slug back to the authored id when it fetches the page. (UCR-3053)
 */
export function learnArticleUrl(article: { id: string; folderPath: string }): string {
    return toCanonicalCase(`${learnFolderUrl(article.folderPath)}/${article.id}`);
}
